// Ops → Automation → Leads: every conversation with a flow is a captured lead / query / survey response.
// Pick a flow → see its sessions (identity, collected values, status, cost, last activity) → drill into one
// to read the full per-turn transcript with diagnostics (extraction, validation, hooks, active branch).
// Data via services/ecapApi (listSessions / getSession). See src/pages/README.md.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PageHeader, Button, Alert } from '../ui/primitives';
import { listFlows, listSessions, getSession, updateLead, getAlerts, ackAlert, getFlowBreakdown, activeTenant, getFlow } from '../services/ecapApi';
import CapabilityNotice from '../components/CapabilityNotice';

const STATUS_TONE = {
  COMPLETE: 'bg-emerald-50 text-emerald-700',
  ACTIVE: 'bg-blue-50 text-blue-700',
  ABANDONED: 'bg-slate-100 text-slate-500',
};
// Operational lifecycle the business works the lead through (distinct from the conversation status).
const LEAD_STATUSES = ['NEW', 'IN_PROGRESS', 'RESOLVED', 'ARCHIVED'];
const LEAD_TONE = {
  NEW: 'bg-blue-50 text-blue-700', IN_PROGRESS: 'bg-amber-50 text-amber-700',
  RESOLVED: 'bg-emerald-50 text-emerald-700', ARCHIVED: 'bg-slate-100 text-slate-400',
};
const LEAD_LABEL = { NEW: 'New', IN_PROGRESS: 'In progress', RESOLVED: 'Resolved', ARCHIVED: 'Archived' };

function fmtTime(ms) {
  if (!ms) return '';
  try { return new Date(ms).toLocaleString(); } catch (e) { return String(ms); }
}
// "how long since we heard from them" is what an operator actually reads off the list.
const MIN = 60000, HOUR = 60 * MIN, DAY = 24 * HOUR;
function ago(ms, now) {
  if (!ms) return '';
  const d = Math.max(0, now - ms);
  if (d < MIN) return 'just now';
  if (d < HOUR) return `${Math.floor(d / MIN)}m ago`;
  if (d < DAY) return `${Math.floor(d / HOUR)}h ago`;
  if (d < 7 * DAY) return `${Math.floor(d / DAY)}d ago`;
  return fmtTime(ms).split(',')[0];
}

// BUG-46/47: which attribute keys are SYSTEM-PRODUCED (not typed by the customer), split by provenance so the
// leads grid can badge them and an operator never mistakes a computed value for what the customer actually said.
//   ai   — derived fields + ai:// hook outputs (inferred/fabricated by an AI — the BUG-46 concern)
//   auto — every OTHER hook's responseMapping outputs (a capability/HTTP/code result: a quote, a fee, a
//          capped/fulfilled quantity like inventory://'s orderQty — the BUG-47 concern; the customer's raw ask,
//          e.g. requestedQty, is preserved as its own produced attribute so a downgrade is visible, not silent).
// A key that's both resolves to `ai` (the stronger provenance flag). Pure + exported for unit testing.
export function producedKeys(flow) {
  const ai = new Set();
  const auto = new Set();
  if (!flow) return { ai, auto };
  (flow.fields || []).forEach((fld) => { if (fld && fld.derived && fld.key) ai.add(fld.key); });
  const scan = (h) => {
    if (!h || !h.responseMapping) return;
    const isAi = typeof h.url === 'string' && h.url.startsWith('ai://');
    Object.keys(h.responseMapping).forEach((k) => (isAi ? ai : auto).add(k));
  };
  (flow.hooks || []).forEach(scan);
  (flow.branches || []).forEach((b) => (b.hooks || []).forEach(scan));
  auto.forEach((k) => { if (ai.has(k)) auto.delete(k); });
  return { ai, auto };
}

// LEAD-STATUS-SAVE-1: the grid works in SessionSummary field names ({leadStatus, assignee, note}); the update
// endpoint's canonical body names status `status`. Translate on the way out.
export function toLeadUpdate(patch) {
  const body = {};
  if (patch.leadStatus !== undefined) body.status = patch.leadStatus;
  if (patch.assignee !== undefined) body.assignee = patch.assignee;
  if (patch.note !== undefined) body.note = patch.note;
  return body;
}

// The fields of an edit the server's response does NOT reflect. A 2xx alone is not proof of a save: the
// status edit used to return 200 with the lead still NEW.
export function unsavedLeadFields(patch, saved) {
  if (!saved) return Object.keys(toLeadUpdate(patch));
  const norm = (v) => (v == null || v === '' ? null : String(v).trim());
  const out = [];
  if (patch.leadStatus !== undefined && norm(saved.leadStatus) !== norm(patch.leadStatus)) out.push('status');
  if (patch.assignee !== undefined && norm(saved.assignee) !== norm(patch.assignee)) out.push('assignee');
  if (patch.note !== undefined && norm(saved.note) !== norm(patch.note)) out.push('note');
  return out;
}

const serverLead = (saved) => ({ leadStatus: saved.leadStatus, assignee: saved.assignee, note: saved.note });

// Where a session came from, as far as the runtime records it. Only Deploy-Demo (__demo) and the Studio tester
// (__draftMode) are marked; every other session started with the flow's public key — the customer booking page
// or any other client holding that key — and cannot be attributed more precisely.
export function sessionSource(s) {
  const v = (s && s.values) || {};
  if (v.__demo === 'true') return 'demo';
  if (v.__draftMode === 'true') return 'studio-test';
  return 'public-key';
}
const SOURCE_LABEL = { 'public-key': 'Public key', demo: 'Demo', 'studio-test': 'Studio test' };
const SOURCE_TITLE = {
  'public-key': 'Started with the flow’s public key (the customer booking page or another client using that key)',
  demo: 'Deploy-Demo session — external actions are blocked',
  'studio-test': 'Studio test chat on the working draft — its actions run for real',
};

function OpsSessions({ authSession, recordSources }) {
  const [sourceFilter, setSourceFilter] = useState(''); // '' | 'public-key' | 'demo' | 'studio-test'
  const [flows, setFlows] = useState([]);
  const [flowId, setFlowId] = useState('');
  const [aiKeys, setAiKeys] = useState(new Set()); // AI-produced attribute keys for the current flow (BUG-46)
  const [autoKeys, setAutoKeys] = useState(new Set()); // capability/hook-computed attribute keys (BUG-47)
  const [sessions, setSessions] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenant] = useState(activeTenant()); // fixed per mount; the shell remounts this page on company switch
  const [leadFilter, setLeadFilter] = useState('ALL');
  const [alerts, setAlerts] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [attrFilters, setAttrFilters] = useState({}); // dynamic: {attributeKey: value} — any captured attr or AI output
  const [filterKey, setFilterKey] = useState('');
  const [saveState, setSaveState] = useState(null); // null | 'saving' | 'saved' | 'error' — lead-edit auto-save feedback
  const [q, setQ] = useState('');                   // free-text across identity, note, assignee and every captured value
  const [owner, setOwner] = useState('');           // '' | 'MINE' | 'UNASSIGNED'
  const [sort, setSort] = useState(null);           // null = default order (hot first, newest first); else {key, dir}
  const [chosenCols, setChosenCols] = useState(null); // null = the default first-4; else an explicit picker selection
  const [showCols, setShowCols] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // sessionIds ticked for a bulk action
  const [now, setNow] = useState(() => Date.now());
  const claims = (authSession && authSession.user) || {};
  const me = claims.email || claims['cognito:username'] || claims.sub || '';


  const loadAlerts = useCallback(() => {
    getAlerts(false, authSession).then((r) => setAlerts(r?.alerts || [])).catch(() => setAlerts([]));
  }, [authSession]);

  // Ack an alert (optimistic) and drill into its lead.
  const handleAlert = async (a) => {
    setAlerts((rows) => rows.filter((x) => x.id !== a.id));
    try { await ackAlert(a.id, authSession); } catch (e) { /* best-effort */ }
    if (a.sessionId) open(a.sessionId);
  };

  // Update a lead's lifecycle in place (optimistic) and keep the drill-in detail in sync.
  const patchLead = async (sid, patch) => {
    // OPSSESSIONS-PATCHLEAD-NOT-REVERTED: snapshot so a failed auto-save reverts to the last server-known
    // state instead of leaving the grid/detail showing an edit that never persisted.
    const prevRow = sessions.find((r) => r.sessionId === sid);
    const prevDetail = detail;
    setSessions((rows) => rows.map((r) => (r.sessionId === sid ? { ...r, ...patch } : r)));
    setDetail((d) => (d?.session?.sessionId === sid ? { ...d, session: { ...d.session, ...patch } } : d));
    // Edits auto-save (no Save button) — surface the write so the user knows it persisted.
    setSaveState('saving');
    try {
      const saved = await updateLead(sid, toLeadUpdate(patch), authSession);
      const unsaved = unsavedLeadFields(patch, saved);
      // Show what the server actually holds, whatever the outcome.
      if (saved) {
        setSessions((rows) => rows.map((r) => (r.sessionId === sid ? { ...r, ...serverLead(saved) } : r)));
        setDetail((d) => (d?.session?.sessionId === sid ? { ...d, session: { ...d.session, ...serverLead(saved) } } : d));
      }
      if (unsaved.length) {
        if (!saved) {
          if (prevRow) setSessions((rows) => rows.map((r) => (r.sessionId === sid ? prevRow : r)));
          setDetail(prevDetail);
        }
        setSaveState('error');
        setError(`The server did not save the lead ${unsaved.join(', ')}. Showing the saved values — retry or reload.`);
        return;
      }
      setSaveState('saved'); setTimeout(() => setSaveState((v) => (v === 'saved' ? null : v)), 2000);
    }
    catch (e) {
      setError(e.message); setSaveState('error');
      if (prevRow) setSessions((rows) => rows.map((r) => (r.sessionId === sid ? prevRow : r)));
      setDetail(prevDetail);
    }
  };

  // Same edit across every ticked lead — the API takes one session at a time, so this is N writes.
  const bulkPatch = async (patch) => {
    const ids = [...selected];
    if (!ids.length) return;
    // OPSSESSIONS-BULKPATCH-NO-BUSY-GUARD: ignore a second bulk action while one is in flight — overlapping
    // conflicting batches (e.g. two status buttons clicked fast) would otherwise race to a nondeterministic
    // per-lead state and their optimistic snapshots/reverts would interleave. The buttons are also disabled.
    if (saveState === 'saving') return;
    const prevRows = sessions; // OPSSESSIONS-BULKPATCH-PARTIAL-MASKED: snapshot for revert
    const prevDetail = detail;
    setSessions((rows) => rows.map((r) => (selected.has(r.sessionId) ? { ...r, ...patch } : r)));
    setDetail((d) => (d?.session && selected.has(d.session.sessionId) ? { ...d, session: { ...d.session, ...patch } } : d));
    setSaveState('saving');
    let done = 0;
    try {
      for (const sid of ids) {
        const saved = await updateLead(sid, toLeadUpdate(patch), authSession);
        const unsaved = unsavedLeadFields(patch, saved);
        if (unsaved.length) throw new Error(`the server did not save the lead ${unsaved.join(', ')}`);
        done += 1;
      }
      setSaveState('saved'); setTimeout(() => setSaveState((v) => (v === 'saved' ? null : v)), 2000);
      setSelected(new Set());
    } catch (e) {
      // The N writes are not atomic; a mid-loop failure previously left later leads showing an edit that
      // never persisted. Revert the whole optimistic batch (grid AND open detail) and report the partial result
      // so the operator reloads to the true state rather than trusting a half-applied grid.
      setSessions(prevRows); setDetail(prevDetail); setSaveState('error');
      setError(`Saved ${done} of ${ids.length} leads, then failed: ${e.message}. Reverted the unsaved edits — reload to confirm, then retry.`);
      load(true);
    }
  };

  useEffect(() => {
    listFlows(authSession).then((f) => {
      setFlows(f || []);
      setFlowId(f && f.length ? f[0].id : '');
    }).catch((e) => setError(e.message));
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession, tenant]); // re-list flows + alerts when the super-admin switches company

  // Tracks the currently-selected flow so an in-flight (esp. the quiet 30s background) list for a PREVIOUS
  // flow can't overwrite the newly-selected flow's grid — which would show another flow's leads (incl. PII).
  const flowIdRef = useRef(flowId);
  flowIdRef.current = flowId;

  // `quiet` is the background refresh: no spinner, and it must not close the open drill-in.
  const load = useCallback(async (quiet) => {
    if (!flowId) return;
    const fid = flowId;
    if (!quiet) { setLoading(true); setError(null); setDetail(null); }
    try {
      const rows = await listSessions(fid, authSession) || [];
      if (fid !== flowIdRef.current) return; // selection changed while this was in flight — drop the stale rows
      setSessions(rows);
    }
    catch (e) { if (!quiet && fid === flowIdRef.current) setError(e.message); }
    finally { if (!quiet) setLoading(false); }
    getFlowBreakdown(fid, authSession)
      .then((b) => { if (fid === flowIdRef.current) setBreakdown(b); })
      .catch(() => { if (fid === flowIdRef.current) setBreakdown(null); });
  }, [flowId, authSession]);

  useEffect(() => { load(); }, [load]);
  // A different flow has a different attribute schema — clear stale filters, columns and ticks so
  // nothing is hidden and no bulk action can land on a lead the operator can't see.
  useEffect(() => { setAttrFilters({}); setFilterKey(''); setChosenCols(null); setSelected(new Set()); }, [flowId]);

  // BUG-46: load the flow to learn which attributes are AI-produced (derived fields + ai:// hook outputs), so
  // the leads grid can badge them "(AI)". Best-effort: on any error, badge nothing extra (still shows _-keys).
  useEffect(() => {
    if (!flowId) { setAiKeys(new Set()); setAutoKeys(new Set()); return; }
    let alive = true;
    getFlow(flowId, authSession).then((f) => {
      if (!alive || !f) return;
      const { ai, auto } = producedKeys(f);
      setAiKeys(ai);
      setAutoKeys(auto);
    }).catch(() => { setAiKeys(new Set()); setAutoKeys(new Set()); });
    return () => { alive = false; };
  }, [flowId, authSession]);

  // Leads arrive while the page is open, and hot-lead alerts are time-critical — refresh both quietly.
  const refreshRef = useRef(null);
  refreshRef.current = () => { load(true); loadAlerts(); };
  useEffect(() => {
    const h = setInterval(() => { setNow(Date.now()); refreshRef.current(); }, 30000);
    return () => clearInterval(h);
  }, []);

  // OPSSESSIONS-OPEN-LOAD-RACE: monotonic token so a slower earlier getSession can't land last and overwrite
  // the lead detail the operator most recently opened (which would show the wrong lead's data + working fields).
  const openSeq = useRef(0);
  const open = async (sid) => {
    const seq = ++openSeq.current;
    setDetail({ loading: true });
    try { const d = await getSession(sid, authSession); if (seq === openSeq.current) setDetail(d); }
    catch (e) { if (seq === openSeq.current) { setError(e.message); setDetail(null); } }
  };

  // Business-captured attributes only; _-prefixed keys are system/AI enrichment, shown separately.
  const isEnrichment = (k) => k.startsWith('_');
  // BUG-46: an attribute is AI-PRODUCED (not customer-supplied) when it's a _-enrichment key, a derived field,
  // or the output of an ai:// hook — so a fabricated/inferred value (e.g. ai://vision damage text) is badged
  // "(AI)" instead of reading as clean customer data. aiKeys is derived from the flow definition below.
  // BUG-46/47: label a produced attribute by provenance — "(AI)" for AI/derived, "(auto)" for a capability/hook
  // result (a quote, a capped/fulfilled quantity), so neither reads as clean customer input at list level.
  const aiLabel = (k) => (isEnrichment(k) ? `${humanizeKey(k)} (AI)`
    : aiKeys.has(k) ? `${k} (AI)`
      : autoKeys.has(k) ? `${k} (auto)` : k);
  // Every attribute key present across the loaded leads — business AND AI-plugin outputs. This is what
  // makes the dashboard dynamic: a new capability's output (e.g. _willPurchase) shows up here with no
  // code change, so it can be shown as a column and filtered on.
  const allKeys = Array.from(new Set(sessions.flatMap((s) => Object.keys(s.values || {})))).sort();
  const distinctFor = (k) => Array.from(new Set(sessions.map((s) => s.values?.[k]).filter((v) => v != null && v !== ''))).sort();
  const baseCols = chosenCols || allKeys.filter((k) => !isEnrichment(k)).slice(0, 4);
  // Any attribute you're actively filtering on also becomes a visible column (incl. AI outputs).
  const columns = [...baseCols, ...Object.keys(attrFilters).filter((k) => !baseCols.includes(k))];
  const toggleCol = (k) => setChosenCols((c) => {
    const cur = c || allKeys.filter((x) => !isEnrichment(x)).slice(0, 4);
    return cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
  });

  // Exports what's on screen (filters and search included) — an export that ignores the filters is a
  // different data set from the one the operator is looking at. Lead-working fields ride along.
  const exportCsv = () => {
    if (!shown.length) return;
    const valueKeys = Array.from(new Set(shown.flatMap((s) => Object.keys(s.values || {}))));
    const head = ['sessionId', 'status', 'leadStatus', 'assignee', 'note', 'identity', 'updatedAt', 'tokens', ...valueKeys];
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const rows = shown.map((s) => [s.sessionId, s.status, leadOf(s), s.assignee || '', s.note || '',
      s.identityValue || '', fmtTime(s.updatedAt),
      s.tokensUsed, ...valueKeys.map((k) => s.values?.[k] || '')].map(esc).join(','));
    const blob = new Blob([[head.map(esc).join(','), ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${flowId}-leads.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const leadOf = (s) => s.leadStatus || 'NEW';
  // Hot lead: AI auto-triage flagged it HIGH (negative sentiment or a hot tag) and it isn't closed out yet.
  const isHot = (s) => s.values?._priority === 'HIGH' && !['RESOLVED', 'ARCHIVED'].includes(leadOf(s));
  const workedOn = sessions.filter((s) => ['IN_PROGRESS', 'RESOLVED'].includes(leadOf(s))).length;
  const hotCount = sessions.filter(isHot).length;
  const passesAttr = (s) => Object.entries(attrFilters).every(([k, v]) => (s.values?.[k] ?? '') === v);
  const needle = q.trim().toLowerCase();
  const passesSearch = (s) => !needle || [s.identityValue, s.sessionId, s.assignee, s.note, s.status,
    ...Object.values(s.values || {})].filter(Boolean).join(' ').toLowerCase().includes(needle);
  const passesOwner = (s) => (owner === 'MINE' ? s.assignee === me : owner === 'UNASSIGNED' ? !s.assignee : true);
  const filtered = (leadFilter === 'ALL' ? sessions
    : leadFilter === 'HOT' ? sessions.filter(isHot)
      : sessions.filter((s) => leadOf(s) === leadFilter))
    .filter(passesAttr).filter(passesSearch).filter(passesOwner)
    .filter((s) => !sourceFilter || sessionSource(s) === sourceFilter);

  // Sort value for a column key — the fixed columns are named, anything else is a captured attribute.
  const sortVal = (s, key) => {
    if (key === 'lead') return LEAD_STATUSES.indexOf(leadOf(s));
    if (key === 'status') return s.status || '';
    if (key === 'identity') return s.identityValue || '';
    if (key === 'sentiment') return s.values?._sentiment || '';
    if (key === 'assignee') return s.assignee || '';
    if (key === 'updatedAt') return s.updatedAt || 0;
    return s.values?.[key] ?? '';
  };
  const shown = [...filtered].sort((a, b) => {
    // Default order: hot leads first so operators triage the unhappy customers before anything else,
    // then newest activity. An explicit header click takes over completely.
    if (!sort) return (isHot(b) ? 1 : 0) - (isHot(a) ? 1 : 0) || (b.updatedAt || 0) - (a.updatedAt || 0);
    const av = sortVal(a, sort.key), bv = sortVal(b, sort.key);
    const c = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sort.dir === 'asc' ? c : -c;
  });
  const sortBy = (key) => setSort((s) => (s && s.key === key
    ? (s.dir === 'desc' ? { key, dir: 'asc' } : null) // third click returns to the default order
    : { key, dir: 'desc' }));
  const arrow = (key) => (sort && sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');

  const allTicked = shown.length > 0 && shown.every((s) => selected.has(s.sessionId));
  const toggleTick = (sid) => setSelected((sel) => { const n = new Set(sel); if (!n.delete(sid)) n.add(sid); return n; });
  const tickAll = () => setSelected(allTicked ? new Set() : new Set(shown.map((s) => s.sessionId)));

  return (
    <div className="p-6">
      <PageHeader title="Leads" subtitle="Captured conversations — work them: assign, progress, resolve" />
      <CapabilityNotice capability={recordSources} kind="leads" />
      {alerts.length > 0 && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50/70 p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-rose-700">🔔 Hot-lead alerts</span>
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-rose-600 text-white">{alerts.length}</span>
            <span className="text-[11px] text-rose-500">— unhappy or urgent leads flagged by AI; pick one up</span>
          </div>
          <div className="space-y-1.5">
            {alerts.map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-sm bg-white rounded-lg border border-rose-100 px-3 py-1.5">
                <span className="text-rose-500">🔥</span>
                <span className="flex-1 text-slate-700">{a.message}</span>
                <span className="text-[11px] text-slate-400 whitespace-nowrap">{fmtTime(a.createdAt)}</span>
                <Button size="sm" variant="outline" onClick={() => handleAlert(a)}>Open &amp; ack</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <select className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          value={flowId} onChange={(e) => setFlowId(e.target.value)}>
          {flows.map((f) => <option key={f.id} value={f.id}>{f.name || f.id}</option>)}
        </select>
        <input className="w-56 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" value={q}
          onChange={(e) => setQ(e.target.value)} placeholder="🔍 Search identity, note, values…" />
        <select className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)} aria-label="Session source">
          <option value="">All sources</option>
          {Object.entries(SOURCE_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <Button size="sm" variant="ghost" onClick={() => load()}>Refresh</Button>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!shown.length}
          title={shown.length !== sessions.length ? `Exports the ${shown.length} leads currently shown` : 'Export these leads'}>⬇ Export CSV</Button>
        <Button size="sm" variant={showBreakdown ? 'primary' : 'outline'} onClick={() => setShowBreakdown((v) => !v)}
          disabled={!breakdown?.attributes?.length}>📊 Breakdown</Button>
        {/* Which captured attributes appear as table columns — a flow can capture far more than fits. */}
        <div className="relative">
          <Button size="sm" variant={chosenCols ? 'primary' : 'outline'} onClick={() => setShowCols((v) => !v)}
            disabled={!allKeys.length}>⚙ Columns ({columns.length})</Button>
          {showCols && (
            <div className="absolute z-20 mt-1 w-64 max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg p-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-wide text-slate-400">Columns</span>
                <button className="text-[11px] text-slate-400 hover:text-slate-600" onClick={() => setChosenCols(null)}>reset</button>
              </div>
              {allKeys.map((k) => (
                <label key={k} className="flex items-center gap-2 text-xs text-slate-600 py-0.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                  <input type="checkbox" checked={columns.includes(k)} onChange={() => toggleCol(k)} />
                  {aiLabel(k)}
                </label>
              ))}
              {allKeys.length === 0 && <div className="text-xs text-slate-300 px-1 py-1">No attributes captured yet.</div>}
            </div>
          )}
        </div>
        <span className="text-xs text-slate-400">
          {shown.length !== sessions.length ? `${shown.length} of ${sessions.length} leads` : `${sessions.length} leads`}
          {' · '}{workedOn} worked on{hotCount > 0 && <span className="text-rose-500 font-medium"> · 🔥 {hotCount} hot</span>}
        </span>
      </div>
      {showBreakdown && breakdown?.attributes?.length > 0 && <BreakdownPanel data={breakdown} />}
      {/* Operational lifecycle filter (+ a hot-lead quick filter when any are flagged) */}
      <div className="flex gap-1.5 mb-4">
        {['ALL', ...(hotCount > 0 ? ['HOT'] : []), ...LEAD_STATUSES].map((k) => {
          const n = k === 'ALL' ? sessions.length : k === 'HOT' ? hotCount : sessions.filter((s) => leadOf(s) === k).length;
          const hot = k === 'HOT';
          const active = leadFilter === k;
          return (
            <button key={k} onClick={() => setLeadFilter(k)}
              className={`px-2.5 py-1 rounded-lg text-xs ${active ? (hot ? 'bg-rose-600 text-white' : 'bg-blue-600 text-white') : (hot ? 'bg-rose-50 text-rose-600 hover:bg-rose-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}`}>
              {k === 'ALL' ? 'All' : hot ? '🔥 Hot' : LEAD_LABEL[k]} {n > 0 && <span className="opacity-70">{n}</span>}
            </button>
          );
        })}
        {/* Ownership — "who is working this" is a different axis from where the lead is in its lifecycle. */}
        <span className="w-px bg-slate-200 mx-1" />
        {[['MINE', 'Mine'], ['UNASSIGNED', 'Unassigned']].map(([k, label]) => {
          const n = sessions.filter((s) => (k === 'MINE' ? s.assignee === me : !s.assignee)).length;
          return (
            <button key={k} onClick={() => setOwner((o) => (o === k ? '' : k))} disabled={k === 'MINE' && !me}
              className={`px-2.5 py-1 rounded-lg text-xs disabled:opacity-40 ${owner === k ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {label} {n > 0 && <span className="opacity-70">{n}</span>}
            </button>
          );
        })}
      </div>

      {/* Bulk actions — working a batch of leads one dropdown at a time is the slow path. */}
      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
          <span className="text-sm text-blue-800 font-medium">{selected.size} selected</span>
          <span className="text-xs text-blue-500">set status</span>
          {LEAD_STATUSES.map((l) => (
            <Button key={l} size="sm" variant="outline" disabled={saveState === 'saving'} onClick={() => bulkPatch({ leadStatus: l })}>{LEAD_LABEL[l]}</Button>
          ))}
          {me && <Button size="sm" variant="outline" disabled={saveState === 'saving'} onClick={() => bulkPatch({ assignee: me })}>Assign to me</Button>}
          <button className="text-xs text-blue-500 hover:text-blue-700 ml-auto" onClick={() => setSelected(new Set())}>clear selection</button>
        </div>
      )}
      {/* Dynamic attribute filters — filter leads by ANY captured attribute or AI-plugin output. */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-[11px] text-slate-400">Filter by:</span>
        <select className="rounded-lg border border-slate-200 px-2 py-1 text-xs" value={filterKey} onChange={(e) => setFilterKey(e.target.value)}>
          <option value="">attribute…</option>
          {allKeys.filter((k) => !(k in attrFilters)).map((k) => (
            <option key={k} value={k}>{aiLabel(k)}</option>
          ))}
        </select>
        {filterKey && (
          <select className="rounded-lg border border-slate-200 px-2 py-1 text-xs" defaultValue=""
            onChange={(e) => { if (e.target.value) { setAttrFilters((f) => ({ ...f, [filterKey]: e.target.value })); setFilterKey(''); } }}>
            <option value="">value…</option>
            {distinctFor(filterKey).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
        {Object.entries(attrFilters).map(([k, v]) => (
          <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 flex items-center gap-1">
            {isEnrichment(k) ? humanizeKey(k) : k} = <b>{v}</b>
            <button className="text-blue-400 hover:text-blue-700" onClick={() => setAttrFilters((f) => { const n = { ...f }; delete n[k]; return n; })}>✕</button>
          </span>
        ))}
        {Object.keys(attrFilters).length > 0 && <button className="text-[11px] text-slate-400 hover:text-slate-600" onClick={() => setAttrFilters({})}>clear all</button>}
      </div>
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_460px] gap-5">
        {/* Leads table */}
        <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={allTicked} disabled={!shown.length} onChange={tickAll}
                    title="Select all shown" />
                </th>
                {[['lead', 'Lead'], ['status', 'Conv.'], ['identity', 'Identity'], ['sentiment', 'Sentiment'],
                  ...columns.map((c) => [c, aiLabel(c)]),
                  ['assignee', 'Assignee'], ['updatedAt', 'Updated']].map(([key, label]) => (
                    <th key={key} className="text-left font-medium px-3 py-2">
                      <button className="hover:text-slate-700" onClick={() => sortBy(key)} title="Sort">{label}{arrow(key)}</button>
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length + 7} className="px-3 py-4 text-slate-400">Loading…</td></tr>}
              {!loading && shown.length === 0 && (
                <tr><td colSpan={columns.length + 7} className="px-3 py-4 text-slate-400">
                  {sessions.length > 0 ? 'No leads match these filters.'
                    : `No leads${leadFilter !== 'ALL' ? ` in ${LEAD_LABEL[leadFilter]}` : ' yet — test the flow to capture one'}.`}
                </td></tr>
              )}
              {shown.map((s) => (
                <tr key={s.sessionId} onClick={() => open(s.sessionId)}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${isHot(s) ? 'bg-rose-50/40' : ''} ${detail?.session?.sessionId === s.sessionId ? 'bg-blue-50/50' : ''}`}>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(s.sessionId)} onChange={() => toggleTick(s.sessionId)} />
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      {isHot(s) && <span title="Hot lead — flagged by AI auto-triage (needs fast follow-up)">🔥</span>}
                      <select value={leadOf(s)} onChange={(e) => patchLead(s.sessionId, { leadStatus: e.target.value })}
                        className={`text-xs px-1.5 py-0.5 rounded border-0 cursor-pointer ${LEAD_TONE[leadOf(s)]}`}>
                        {LEAD_STATUSES.map((l) => <option key={l} value={l}>{LEAD_LABEL[l]}</option>)}
                      </select>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_TONE[s.status] || 'bg-slate-100 text-slate-500'}`}>{s.status}</span>
                    {sessionSource(s) !== 'public-key' && (
                      <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700" title={SOURCE_TITLE[sessionSource(s)]}>
                        {SOURCE_LABEL[sessionSource(s)]}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{s.identityValue || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2"><SentimentBadge value={s.values?._sentiment} /></td>
                  {columns.map((c) => <td key={c} className="px-3 py-2 text-slate-600 truncate max-w-[160px]">{s.values?.[c] || ''}</td>)}
                  <td className="px-3 py-2 text-slate-600 text-xs whitespace-nowrap">
                    {s.assignee ? (s.assignee === me ? <span className="text-blue-700">you</span> : s.assignee) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs whitespace-nowrap" title={fmtTime(s.updatedAt)}>{ago(s.updatedAt, now)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Drill-in: values + transcript */}
        <div>
          {!detail && <div className="text-sm text-slate-400 mt-1">Select a session to see its transcript and captured data.</div>}
          {detail?.loading && <div className="text-sm text-slate-400">Loading…</div>}
          {detail && !detail.loading && <SessionDetail detail={detail} onPatch={patchLead} saveState={saveState} aiKeys={aiKeys} autoKeys={autoKeys} />}
        </div>
      </div>
    </div>
  );
}

function SessionDetail({ detail, onPatch, saveState, aiKeys, autoKeys }) {
  const ai = aiKeys || new Set();
  const auto = autoKeys || new Set();
  const s = detail.session || {};
  const turns = detail.turns || [];
  const lead = s.leadStatus || 'NEW';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-slate-800">Lead detail</div>
          {saveState === 'saving' && <span className="text-[11px] text-slate-400">Saving…</span>}
          {saveState === 'saved' && <span className="text-[11px] text-emerald-600">Saved ✓</span>}
          {saveState === 'error' && <span className="text-[11px] text-rose-600">Save failed</span>}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded ${STATUS_TONE[s.status] || 'bg-slate-100 text-slate-500'}`}>{s.status}</span>
      </div>
      <div className="text-xs text-slate-400 mb-3">{s.sessionId} · {s.tokensUsed} tokens · {fmtTime(s.updatedAt)}</div>

      {/* Work the lead: lifecycle status, assignee, note */}
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Work the lead · changes save automatically</div>
      <div className="mb-4 rounded-lg border border-slate-200 p-2.5 grid grid-cols-2 gap-2">
        <label className="text-[11px] text-slate-500">Lead status
          <select className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" value={lead}
            onChange={(e) => onPatch(s.sessionId, { leadStatus: e.target.value })}>
            {LEAD_STATUSES.map((l) => <option key={l} value={l}>{LEAD_LABEL[l]}</option>)}
          </select>
        </label>
        <label className="text-[11px] text-slate-500">Assignee
          <input className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" defaultValue={s.assignee || ''}
            placeholder="unassigned" onBlur={(e) => e.target.value !== (s.assignee || '') && onPatch(s.sessionId, { assignee: e.target.value })} />
        </label>
        <label className="text-[11px] text-slate-500 col-span-2">Note
          <textarea className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" rows={2} defaultValue={s.note || ''}
            placeholder="Internal note about this lead…" onBlur={(e) => e.target.value !== (s.note || '') && onPatch(s.sessionId, { note: e.target.value })} />
        </label>
      </div>

      <AiInsight values={s.values} />

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Captured data</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-4">
        {Object.entries(s.values || {}).filter(([k]) => !k.startsWith('_')).map(([k, v]) => (
          <div key={k} className="text-sm flex justify-between gap-2 border-b border-slate-50 py-0.5">
            <span className="text-slate-400">
              {k}{ai.has(k)
                ? <span className="ml-1 text-[10px] font-semibold text-indigo-500 bg-indigo-50 rounded px-1 py-0.5" title="AI-produced — inferred/derived or an AI-hook output, not customer-entered">AI</span>
                : auto.has(k)
                  ? <span className="ml-1 text-[10px] font-semibold text-amber-600 bg-amber-50 rounded px-1 py-0.5" title="System-computed — produced by a capability/hook (e.g. a quote, or a capped/fulfilled quantity), not the customer's raw request">auto</span>
                  : null}
            </span>
            <span className="text-slate-800 font-medium truncate">{v}</span>
          </div>
        ))}
        {Object.keys(s.values || {}).filter((k) => !k.startsWith('_')).length === 0 && <div className="text-sm text-slate-300">nothing captured</div>}
      </div>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Transcript ({turns.length} turns)</div>
      <div className="space-y-2 max-h-[46vh] overflow-auto pr-1">
        {turns.map((t) => (
          <div key={t.turnIndex} className="rounded-lg bg-slate-50 p-2.5 text-sm">
            {t.userInput && <div className="text-slate-700"><b className="text-slate-400 text-xs">user</b> {t.userInput}</div>}
            <div className="text-slate-600"><b className="text-slate-400 text-xs">bot</b> {t.reply}</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(t.extracted || {}).map(([k, v]) => <Tag key={k} tone="blue">+{k}={v}</Tag>)}
              {Object.entries(t.errors || {}).map(([k]) => <Tag key={k} tone="rose">✗ {k}</Tag>)}
              {(t.hooks || []).map((h, i) => {
                const failed = /=FAIL\(/.test(h); // TraceService encodes name=ok(..) | name=FAIL(..) (BUG-45)
                return <Tag key={i} tone={failed ? 'rose' : 'amber'}>{failed ? '✗' : '⚡'} {h}</Tag>;
              })}
              {t.branchId && <Tag tone="purple">⑃ {t.branchId}</Tag>}
              {t.latencyMs != null && <Tag>{t.latencyMs}ms</Tag>}
            </div>
            {(t.richContent || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {/* BUG-44: the interactive components the customer was actually shown (Quote/FileUpload/PayNow…) */}
                {t.richContent.map((c, i) => <Tag key={i} tone="emerald">▣ {c}</Tag>)}
              </div>
            )}
          </div>
        ))}
        {turns.length === 0 && <div className="text-sm text-slate-300">No turns recorded.</div>}
      </div>
    </div>
  );
}

// Flow-specific operational view: group this flow's leads by any captured attribute (driver / rating /
// issue type). The operator picks the attribute; each value shows a proportional bar + count.
function BreakdownPanel({ data }) {
  const attrs = data.attributes || [];
  const [attr, setAttr] = useState(attrs[0]?.attr || '');
  const current = attrs.find((a) => a.attr === attr) || attrs[0];
  const max = Math.max(1, ...(current?.values || []).map((v) => v.count));
  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-sm font-semibold text-slate-700">📊 Operational breakdown</span>
        <span className="text-[11px] text-slate-400">group {data.totalLeads} leads by</span>
        <select className="rounded-lg border border-slate-200 px-2 py-1 text-sm" value={attr} onChange={(e) => setAttr(e.target.value)}>
          {attrs.map((a) => <option key={a.attr} value={a.attr}>{a.attr} ({a.distinctValues})</option>)}
        </select>
      </div>
      <div className="space-y-1">
        {(current?.values || []).map((v) => (
          <div key={v.value} className="flex items-center gap-2 text-sm">
            <span className="w-40 truncate text-slate-600" title={v.value}>{v.value}</span>
            <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden">
              <div className="h-4 bg-blue-500/80 rounded" style={{ width: `${Math.round((v.count / max) * 100)}%` }} />
            </div>
            <span className="w-8 text-right text-slate-500 tabular-nums">{v.count}</span>
          </div>
        ))}
        {current?.distinctValues > current?.values?.length && (
          <div className="text-[11px] text-slate-400 pt-1">showing top {current.values.length} of {current.distinctValues} values</div>
        )}
      </div>
    </div>
  );
}

function Tag({ children, tone = 'slate' }) {
  const tones = { slate: 'bg-slate-100 text-slate-500', blue: 'bg-blue-50 text-blue-600', rose: 'bg-rose-50 text-rose-600', amber: 'bg-amber-50 text-amber-700', purple: 'bg-purple-50 text-purple-700', emerald: 'bg-emerald-50 text-emerald-700' };
  return <span className={`text-[11px] px-1.5 py-0.5 rounded ${tones[tone]}`}>{children}</span>;
}

// Dynamic AI-insight panel: renders WHATEVER _-prefixed enrichment a flow's opted-in AI plugins produced
// (sentiment / summary / tags get special formatting; any other plugin output — confidence, next-action,
// or a future plugin — shows as a labeled row automatically, so the dashboard never needs hardcoding).
const AI_SPECIAL = new Set(['_sentiment', '_priority', '_tags', '_summary']);
const humanizeKey = (k) => k.replace(/^_/, '').replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
function AiInsight({ values }) {
  const enrich = Object.entries(values || {}).filter(([k, v]) => k.startsWith('_') && v != null && v !== '');
  if (enrich.length === 0) return null;
  const has = (k) => values[k];
  const extras = enrich.filter(([k]) => !AI_SPECIAL.has(k));
  return (
    <div className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50/50 p-2.5">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-500">AI insight</span>
        <span className="text-[10px] px-1.5 rounded bg-indigo-100 text-indigo-600">plugins</span>
        {has('_sentiment') && <SentimentBadge value={values._sentiment} />}
        {values._priority === 'HIGH' && <span className="text-[10px] px-1.5 rounded bg-rose-100 text-rose-700 font-semibold">🔥 hot — needs fast follow-up</span>}
      </div>
      {has('_summary') && <div className="text-sm text-slate-700">{values._summary}</div>}
      {has('_tags') && <div className="flex flex-wrap gap-1 mt-1">{values._tags.split(',').map((t) => <Tag key={t} tone="purple">{t.trim()}</Tag>)}</div>}
      {extras.length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1.5">
          {extras.map(([k, v]) => (
            <div key={k} className="text-sm flex justify-between gap-2 border-t border-indigo-50 pt-0.5">
              <span className="text-indigo-400">{humanizeKey(k)}</span><span className="text-slate-800 font-medium truncate" title={v}>{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SentimentBadge({ value }) {
  if (!value) return <span className="text-slate-300">—</span>;
  const tone = value === 'POSITIVE' ? 'bg-emerald-50 text-emerald-700'
    : value === 'NEGATIVE' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-500';
  return <span className={`text-xs px-2 py-0.5 rounded ${tone}`}>{value.toLowerCase()}</span>;
}

export default OpsSessions;
